import { useRef, useState } from "react";
import { LINGUE, type Lingua } from "../../i18n/index.ts";
import { linkProps, navigate, type SezioneImpostazioni } from "../lib/router.ts";
import type { Permission, Session } from "../lib/types.ts";
import { SezioneUtenti } from "./Users.tsx";
import { SezioneAccessi } from "./Accessi.tsx";
import type { Chiave } from "../../i18n/index.ts";
import { Apribile, Badge, Button, Card, ErrorNotice, Field, Input, Spinner, Textarea } from "../components/ui.tsx";
import { api, toApiError } from "../lib/api.ts";
import { useT } from "../lib/i18n.tsx";
import { useAction, useQuery } from "../lib/hooks.ts";
import type { Cartella, ElencoCartelle, ImpostazioniEmail, ReadTokenSummary, Registry, Stack } from "../lib/types.ts";
import { formatoData } from "./Stacks.tsx";

/**
 * Amministrazione dell'installazione: registry, cartelle dell'host e database.
 *
 * Sezioni che hanno poco in comune tranne la cosa che conta: si toccano di
 * rado, e quando si toccano si sta amministrando Portman, non usandolo.
 */

/** Ogni sezione con il permesso che la apre e il titolo che la nomina nel menu. */
const SEZIONI: { id: SezioneImpostazioni; titolo: Chiave; permesso: Permission }[] = [
  { id: "utenti", titolo: "utenti.titolo", permesso: "user:manage" },
  { id: "accessi", titolo: "accessi.titolo", permesso: "settings:manage" },
  { id: "registry", titolo: "registry.titolo", permesso: "settings:manage" },
  { id: "token", titolo: "token.titolo", permesso: "settings:manage" },
  { id: "email", titolo: "email.titoloSezione", permesso: "settings:manage" },
  { id: "cartelle", titolo: "cartelle.titolo", permesso: "settings:manage" },
  { id: "backup", titolo: "database.titolo", permesso: "settings:manage" },
  { id: "generale", titolo: "generale.titolo", permesso: "settings:manage" },
];

export function ImpostazioniPage({ sezione, sessione }: { sezione: SezioneImpostazioni | null; sessione: Session }) {
  const t = useT();
  const permesse = SEZIONI.filter((voce) => sessione.permissions.includes(voce.permesso));
  // Senza sezione, o con una che non si può aprire, si apre la prima permessa.
  const attiva = permesse.find((voce) => voce.id === sezione) ?? permesse[0];
  const apri = (id: SezioneImpostazioni) => navigate(`/impostazioni/${id}`);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">{t.t("nav.impostazioni")}</h1>
        <p className="text-sm tenue">{t.t("impostazioni.intro")}</p>
      </div>

      {!attiva && <Card className="px-4 py-6 text-center text-sm tenue">{t.t("impostazioni.nessunaSezione")}</Card>}

      {attiva && (
        <div className="grid gap-6 md:grid-cols-[13rem_minmax(0,1fr)]">
          <select
            aria-label={t.t("impostazioni.sezioni")}
            value={attiva.id}
            onChange={(e) => apri(e.target.value as SezioneImpostazioni)}
            className="rounded-lg border bg-[var(--superficie)] px-3 py-2 text-sm md:hidden"
          >
            {permesse.map((voce) => (
              <option key={voce.id} value={voce.id}>
                {t.t(voce.titolo)}
              </option>
            ))}
          </select>

          <nav aria-label={t.t("impostazioni.sezioni")} className="hidden space-y-1 self-start text-sm md:block">
            {permesse.map((voce) => (
              <a
                key={voce.id}
                {...linkProps(`/impostazioni/${voce.id}`)}
                aria-current={voce.id === attiva.id ? "page" : undefined}
                className={
                  voce.id === attiva.id
                    ? "block rounded-lg bg-accent-500/12 px-3 py-1.5 font-medium text-accent-700 dark:text-accent-500"
                    : "block rounded-lg px-3 py-1.5 tenue transition-colors hover:bg-[var(--superficie-tenue)] hover:text-[var(--testo)]"
                }
              >
                {t.t(voce.titolo)}
              </a>
            ))}
          </nav>

          <div className="min-w-0">
            {attiva.id === "utenti" && <SezioneUtenti sessione={sessione} />}
            {attiva.id === "accessi" && <SezioneAccessi />}
            {attiva.id === "registry" && <SezioneRegistry />}
            {attiva.id === "token" && <SezioneToken />}
            {attiva.id === "email" && <SezioneEmail />}
            {attiva.id === "cartelle" && <SezioneCartelle />}
            {attiva.id === "backup" && <SezioneDatabase />}
            {attiva.id === "generale" && <SezioneGenerale />}
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Generale -------------------------------------------------------------- */

function SezioneGenerale() {
  const { data, error, loading, reload } = useQuery<{ lingua: Lingua; daAmbiente: boolean }>("/impostazioni/lingua");
  const t = useT();

  const cambio = useAction(async (lingua: Lingua) => {
    await api.put("/impostazioni/lingua", { lingua });
    reload();
  });

  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-medium">{t.t("generale.titolo")}</h2>
        <p className="text-sm tenue">{t.t("generale.intro")}</p>
      </div>

      <ErrorNotice error={error ?? cambio.error} />
      {loading && !data && <Spinner className="tenue" />}

      {data && (
        <Card className="p-5">
          <Field label={t.t("generale.lingua")} hint={data.daAmbiente ? t.t("generale.daAmbiente") : undefined}>
            <select
              value={data.lingua}
              disabled={data.daAmbiente || cambio.running}
              onChange={(e) => void cambio.run(e.target.value as Lingua)}
              className="w-full max-w-xs rounded-lg border bg-[var(--superficie)] px-3 py-2 text-sm disabled:opacity-60"
            >
              {LINGUE.map((lingua) => (
                <option key={lingua.codice} value={lingua.codice}>
                  {lingua.nome}
                </option>
              ))}
            </select>
          </Field>
        </Card>
      )}
    </section>
  );
}

/* --- Registry -------------------------------------------------------------- */

function SezioneRegistry() {
  const { data, error, loading, reload } = useQuery<{ registries: Registry[] }>(
    "/impostazioni/registries",
  );
  const [apertura, setApertura] = useState(false);
  const t = useT();

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-medium">{t.t("registry.titolo")}</h2>
          <p className="text-sm tenue">{t.t("registry.intro")}</p>
        </div>

        <Button variant="primary" onClick={() => setApertura((aperta) => !aperta)}>
          {t.t(apertura ? "comune.annulla" : "registry.aggiungi")}
        </Button>
      </div>

      {apertura && (
        <FormRegistry
          onSalvato={() => {
            setApertura(false);
            reload();
          }}
        />
      )}

      <ErrorNotice error={error} />
      {loading && <Spinner className="tenue" />}

      {data && data.registries.length === 0 && !apertura && (
        <Card className="px-4 py-6 text-center text-sm tenue">{t.t("registry.vuoto")}</Card>
      )}

      {data && data.registries.length > 0 && (
        <Card className="divide-y overflow-hidden">
          {data.registries.map((registry) => (
            <RigaRegistry key={registry.id} registry={registry} onCambiato={reload} />
          ))}
        </Card>
      )}
    </section>
  );
}

function RigaRegistry({ registry, onCambiato }: { registry: Registry; onCambiato: () => void }) {
  const t = useT();
  const eliminazione = useAction(async () => {
    await api.delete(`/impostazioni/registries/${registry.id}`);
    onCambiato();
  });

  return (
    <div className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <code className="font-mono text-[13px]">{registry.server}</code>
        <Badge>{registry.username}</Badge>

        <span className="ml-auto flex items-center gap-3">
          <span className="hidden text-xs tenue sm:inline">
            {t.t("stacks.aggiornato", { quando: formatoData(registry.updatedAt, t) })}
          </span>

          <Button
            variant="ghost"
            className="text-xs text-red-600"
            loading={eliminazione.running}
            onClick={() => void eliminazione.run()}
          >
            {t.t("comune.elimina")}
          </Button>
        </span>
      </div>

      <ErrorNotice error={eliminazione.error} />
    </div>
  );
}

function FormRegistry({ onSalvato }: { onSalvato: () => void }) {
  const [server, setServer] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const t = useT();

  const salvataggio = useAction(async () => {
    await api.put("/impostazioni/registries", { server, username, password });
    onSalvato();
  });

  return (
    <Card className="space-y-4 p-5">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          label={t.t("registry.indirizzo")}
          hint={t.t("registry.indirizzoAiuto")}
        >
          <Input
            value={server}
            onChange={(e) => setServer(e.target.value)}
            placeholder="registry.example.com" // i18n-ignora
            autoFocus
          />
        </Field>

        <Field label={t.t("registry.utente")}>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} />
        </Field>

        <Field label={t.t("registry.password")} hint={t.t("registry.passwordAiuto")}>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      </div>

      <ErrorNotice error={salvataggio.error} />

      <Button variant="primary" loading={salvataggio.running} onClick={() => void salvataggio.run()}>
        {t.t("registry.salva")}
      </Button>
    </Card>
  );
}

/* --- Token di lettura ------------------------------------------------------ */

const DURATE = [
  ["1h", "token.durata1h"],
  ["1g", "token.durata1g"],
  ["7g", "token.durata7g"],
  ["30g", "token.durata30g"],
] as const;

/** Stato dedotto dai campi: la revoca prevale, poi scadenza, creatore e stack. */
function statoToken(token: ReadTokenSummary): {
  testo: Chiave;
  tono: "success" | "warning" | "danger" | "neutral";
} {
  if (token.revokedAt) return { testo: "token.revocato", tono: "neutral" };
  if (new Date(token.expiresAt).getTime() <= Date.now()) return { testo: "token.scaduto", tono: "warning" };
  if (!token.creatorValid) return { testo: "token.creatoreNonValido", tono: "danger" };
  // Senza stack associati il token non legge nulla: non va mostrato come attivo.
  if (token.stacks.length === 0) return { testo: "token.senzaStack", tono: "neutral" };
  return { testo: "ci.attivo", tono: "success" };
}

function SezioneToken() {
  const { data, error, loading, reload } = useQuery<{ tokens: ReadTokenSummary[] }>("/token-lettura");
  const [apertura, setApertura] = useState(false);
  // Il valore esiste solo qui, in memoria: il server ne tiene soltanto l'hash.
  const [creato, setCreato] = useState<string | null>(null);
  const t = useT();

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-medium">{t.t("token.titolo")}</h2>
          <p className="text-sm tenue">{t.t("token.intro")}</p>
        </div>

        <Button variant="primary" onClick={() => setApertura((aperta) => !aperta)}>
          {t.t(apertura ? "comune.annulla" : "token.crea")}
        </Button>
      </div>

      {apertura && (
        <FormToken
          onCreato={(token) => {
            setApertura(false);
            setCreato(token);
            reload();
          }}
        />
      )}

      {creato && <TokenCreato token={creato} onChiudi={() => setCreato(null)} />}

      {/* Aperta da sé quando c'è un token appena creato: è il momento in cui serve. */}
      <GuidaToken aperta={creato !== null} />

      <ErrorNotice error={error} />
      {loading && !data && <Spinner className="tenue" />}

      {data && data.tokens.length === 0 && !apertura && (
        <Card className="px-4 py-6 text-center text-sm tenue">{t.t("token.vuoto")}</Card>
      )}

      {data && data.tokens.length > 0 && (
        <Card className="divide-y overflow-hidden">
          {data.tokens.map((token) => (
            <RigaToken key={token.id} token={token} onCambiato={reload} />
          ))}
        </Card>
      )}
    </section>
  );
}

function RigaToken({ token, onCambiato }: { token: ReadTokenSummary; onCambiato: () => void }) {
  const t = useT();
  const stato = statoToken(token);
  const revoca = useAction(async () => {
    await api.delete(`/token-lettura/${token.id}`);
    onCambiato();
  });

  return (
    <div className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-medium">{token.name}</span>
        <Badge tone={stato.tono}>{t.t(stato.testo)}</Badge>

        {stato.testo !== "token.revocato" && stato.testo !== "token.scaduto" && (
          <Button
            variant="ghost"
            className="ml-auto text-xs text-red-600"
            loading={revoca.running}
            onClick={() => {
              if (window.confirm(t.t("token.revocaConferma", { nome: token.name }))) {
                void revoca.run();
              }
            }}
          >
            {t.t("ci.revoca")}
          </Button>
        )}
      </div>

      <p className="text-xs tenue">
        {token.stacks.length > 0 ? token.stacks.map((s) => s.name).join(", ") : t.t("token.nessunoStack")}
        {" · "}
        {t.t("token.scade", { quando: formatoData(token.expiresAt, t) })}
        {" · "}
        {token.lastUsedAt
          ? t.t("token.ultimoUso", { quando: formatoData(token.lastUsedAt, t), ip: token.lastUsedIp })
          : t.t("ci.maiUsato")}
        {" · "}
        {t.t("token.creatoDa", { chi: token.createdById === null ? t.t("token.creatoreRimosso") : token.createdBy })}
      </p>

      <ErrorNotice error={revoca.error} />
    </div>
  );
}

function FormToken({ onCreato }: { onCreato: (token: string) => void }) {
  const { data: stacks } = useQuery<{ stacks: Stack[] }>("/stacks");
  const [nome, setNome] = useState("");
  const [scelti, setScelti] = useState<number[]>([]);
  const [durata, setDurata] = useState<string>("7g");
  const t = useT();

  const creazione = useAction(async () => {
    const r = await api.post<{ token: string }>("/token-lettura", {
      nome,
      stackIds: scelti,
      durata,
    });
    onCreato(r.token);
  });

  return (
    <Card className="space-y-4 p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t.t("nuovo.nome")} hint={t.t("token.nomeAiuto")}>
          <Input value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
        </Field>

        <Field label={t.t("token.durata")} hint={t.t("token.durataAiuto")}>
          <select
            className="w-full rounded-lg border bg-[var(--superficie)] px-3 py-2 text-sm"
            value={durata}
            onChange={(e) => setDurata(e.target.value)}
          >
            {DURATE.map(([valore, etichetta]) => (
              <option key={valore} value={valore}>
                {t.t(etichetta)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">{t.t("token.stackLeggibili")}</legend>
        {stacks?.stacks.length === 0 && <p className="text-xs tenue">{t.t("token.nessunoStackDisponibile")}</p>}
        {stacks?.stacks.map((stack) => (
          <label key={stack.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={scelti.includes(stack.id)}
              onChange={(e) =>
                setScelti((attuali) =>
                  e.target.checked ? [...attuali, stack.id] : attuali.filter((id) => id !== stack.id),
                )
              }
            />
            {stack.name}
          </label>
        ))}
      </fieldset>

      <ErrorNotice error={creazione.error} />

      <Button
        variant="primary"
        loading={creazione.running}
        disabled={!nome.trim() || scelti.length === 0}
        onClick={() => void creazione.run()}
      >
        {t.t("token.crea")}
      </Button>
    </Card>
  );
}

function TokenCreato({ token, onChiudi }: { token: string; onChiudi: () => void }) {
  const [copiato, setCopiato] = useState(false);
  const [copiaFallita, setCopiaFallita] = useState(false);
  const t = useT();
  return (
    <div className="space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/8 px-3.5 py-3 text-sm">
      <p className="font-medium">{t.t("token.copialoOra")}</p>
      <code className="block break-all font-mono text-[13px]">{token}</code>
      <div className="flex gap-3">
        <Button
          onClick={() => {
            // `navigator.clipboard` manca fuori dai contesti sicuri (http non loopback).
            if (!navigator.clipboard) return setCopiaFallita(true);
            navigator.clipboard.writeText(token).then(
              () => setCopiato(true),
              () => setCopiaFallita(true),
            );
          }}
        >
          {t.t(copiato ? "comune.copiato" : "token.copia")}
        </Button>
        <Button variant="ghost" onClick={onChiudi}>
          {t.t("comune.chiudi")}
        </Button>
      </div>
      {copiaFallita && <p className="text-xs">{t.t("token.copiaFallita")}</p>}
    </div>
  );
}

/**
 * Come impostare un token su chi lo userà.
 *
 * Gli esempi leggono il token da un file e non lo scrivono mai per esteso: un
 * comando con il valore dentro finisce nella cronologia della shell, e da lì
 * in un backup o in uno schermo condiviso.
 */
function GuidaToken({ aperta }: { aperta: boolean }) {
  const base = `${window.location.origin}/api/esterno`;
  const bearer = `-H "Authorization: Bearer $(cat ~/.portman-token)"`;
  const t = useT();

  return (
    <Apribile titolo={t.t("impostazioni.comeFunziona")} apertaIniziale={aperta}>
      <ol className="list-decimal space-y-3 pl-5 text-sm">
        <li>{t.t("guida.passo1")}</li>
        <li>
          {t.t("guida.passo2")}
          <pre className="mt-1 overflow-x-auto font-mono text-xs">
            pbpaste {">"} ~/.portman-token && chmod 600 ~/.portman-token
          </pre>
          {t.t("guida.passo2Altrove")}
        </li>
        <li>
          {t.t("guida.passo3")}
          <pre className="mt-1 overflow-x-auto font-mono text-xs">
            curl -s {bearer} {base}/stacks
          </pre>
          {t.t("guida.passo3Log")}
          <pre className="mt-1 overflow-x-auto font-mono text-xs">
            curl -sN {bearer} "{base}/stacks/&lt;id&gt;/logs?follow=true&amp;tail=200"
          </pre>
        </li>
        <li>{t.t("guida.passo4")}</li>
        <li>{t.t("guida.passo5")}</li>
      </ol>
    </Apribile>
  );
}

/* --- Email e avvisi -------------------------------------------------------- */

function SezioneEmail() {
  const { data, error, loading, reload } = useQuery<ImpostazioniEmail>("/impostazioni/notifiche");
  const t = useT();

  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-medium">{t.t("email.titoloSezione")}</h2>
        <p className="text-sm tenue">{t.t("email.intro")}</p>
      </div>

      <ErrorNotice error={error} />
      {loading && !data && <Spinner className="tenue" />}
      {data && <FormEmail iniziale={data} onSalvato={reload} />}
    </section>
  );
}

const num = (testo: string) => (testo.trim() === "" ? NaN : Number(testo));

function FormEmail({ iniziale, onSalvato }: { iniziale: ImpostazioniEmail; onSalvato: () => void }) {
  const [host, setHost] = useState(iniziale.host);
  const [porta, setPorta] = useState(String(iniziale.porta));
  const [sicurezza, setSicurezza] = useState(iniziale.sicurezza);
  const [utente, setUtente] = useState(iniziale.utente);
  const [password, setPassword] = useState("");
  const [mittente, setMittente] = useState(iniziale.mittente);
  // Con l'indirizzo vuoto si propone quello da cui si sta usando Portman.
  const [indirizzo, setIndirizzo] = useState(iniziale.indirizzoPubblico || location.origin);
  const [destinatari, setDestinatari] = useState(iniziale.destinatari.join("\n"));
  const [soglie, setSoglie] = useState({
    cpuPct: String(iniziale.soglie.cpuPct),
    cpuMinuti: String(iniziale.soglie.cpuMinuti),
    memPct: String(iniziale.soglie.memPct),
    memMinuti: String(iniziale.soglie.memMinuti),
  });
  const [salvato, setSalvato] = useState(false);
  const [inviata, setInviata] = useState(false);
  const t = useT();

  const modificato =
    host !== iniziale.host ||
    porta !== String(iniziale.porta) ||
    sicurezza !== iniziale.sicurezza ||
    utente !== iniziale.utente ||
    password !== "" ||
    mittente !== iniziale.mittente ||
    indirizzo !== (iniziale.indirizzoPubblico || location.origin) ||
    destinatari !== iniziale.destinatari.join("\n") ||
    (Object.keys(soglie) as (keyof typeof soglie)[]).some((k) => soglie[k] !== String(iniziale.soglie[k]));

  const imposta = (campo: keyof typeof soglie) => (e: { target: { value: string } }) => {
    setSoglie((s) => ({ ...s, [campo]: e.target.value }));
  };

  const salvataggio = useAction(async () => {
    await api.put("/impostazioni/notifiche", {
      host,
      porta: num(porta),
      sicurezza,
      utente,
      // Vuota = il server conserva quella già salvata.
      ...(password ? { password } : {}),
      mittente,
      indirizzoPubblico: indirizzo,
      destinatari: destinatari.split("\n").map((r) => r.trim()).filter(Boolean),
      soglie: {
        cpuPct: num(soglie.cpuPct),
        cpuMinuti: num(soglie.cpuMinuti),
        memPct: num(soglie.memPct),
        memMinuti: num(soglie.memMinuti),
      },
    });
    setPassword("");
    setSalvato(true);
    setInviata(false);
    // L'esito della prova riguardava la configurazione di prima.
    prova.clearError();
    onSalvato();
  });

  const prova = useAction(async () => {
    setInviata(false);
    await api.post("/impostazioni/notifiche/prova", {});
    setInviata(true);
  });

  return (
    <Card className="space-y-4 p-5">
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="sm:col-span-2">
          <Field label={t.t("email.server")}>
            <Input value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.example.com" // i18n-ignora
            />
          </Field>
        </div>

        <Field label={t.t("email.porta")}>
          <Input type="number" value={porta} onChange={(e) => setPorta(e.target.value)} />
        </Field>

        <Field label={t.t("email.sicurezza")}>
          <select
            className="w-full rounded-lg border bg-[var(--superficie)] px-3 py-2 text-sm"
            value={sicurezza}
            onChange={(e) => setSicurezza(e.target.value as ImpostazioniEmail["sicurezza"])}
          >
            <option value="nessuna">{t.t("email.sicurezzaNessuna")}</option>
            <option value="starttls">STARTTLS</option>
            <option value="tls">TLS</option>
          </select>
        </Field>

        <div className="sm:col-span-2">
          <Field label={t.t("registry.utente")}>
            <Input value={utente} onChange={(e) => setUtente(e.target.value)} autoComplete="off" />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label={t.t("accesso.password")}>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={iniziale.passwordImpostata ? t.t("email.passwordImpostata") : ""}
              autoComplete="new-password"
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label={t.t("email.mittente")} hint={t.t("email.mittenteAiuto")}>
            <Input value={mittente} onChange={(e) => setMittente(e.target.value)} placeholder="portman@example.com" // i18n-ignora
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label={t.t("email.indirizzoPubblico")} hint={t.t("email.indirizzoPubblicoAiuto")}>
            <Input value={indirizzo} onChange={(e) => setIndirizzo(e.target.value)} />
          </Field>
        </div>
      </div>

      <Field label={t.t("email.destinatari")} hint={t.t("email.destinatariAiuto")}>
        <Textarea rows={3} value={destinatari} onChange={(e) => setDestinatari(e.target.value)} />
      </Field>

      <div className="grid gap-4 sm:grid-cols-4">
        <Field label={t.t("email.cpuOltre")}>
          <Input type="number" value={soglie.cpuPct} onChange={imposta("cpuPct")} />
        </Field>
        <Field label={t.t("email.perMinuti")}>
          <Input type="number" value={soglie.cpuMinuti} onChange={imposta("cpuMinuti")} />
        </Field>
        <Field label={t.t("email.memoriaOltre")}>
          <Input type="number" value={soglie.memPct} onChange={imposta("memPct")} />
        </Field>
        <Field label={t.t("email.perMinuti")}>
          <Input type="number" value={soglie.memMinuti} onChange={imposta("memMinuti")} />
        </Field>
      </div>

      <ErrorNotice error={salvataggio.error} />
      <ErrorNotice error={prova.error} />
      {salvato && !modificato && <p className="text-sm text-emerald-700 dark:text-emerald-400">{t.t("comune.salvato")}</p>}
      {inviata && <p className="text-sm text-emerald-700 dark:text-emerald-400">{t.t("email.provaInviata")}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          loading={salvataggio.running}
          disabled={!modificato}
          onClick={() => void salvataggio.run()}
        >
          {t.t("comune.salva")}
        </Button>

        <Button loading={prova.running} disabled={modificato} onClick={() => void prova.run()}>
          {t.t("email.inviaProva")}
        </Button>

        {modificato && <span className="text-xs tenue">{t.t("email.salvaPrima")}</span>}
        {prova.running && <span className="text-xs tenue">{t.t("email.connessione")}</span>}
      </div>
    </Card>
  );
}

/* --- Cartelle -------------------------------------------------------------- */

function SezioneCartelle() {
  const [percorso, setPercorsoGrezzo] = useState("");
  const [nome, setNome] = useState("");
  const [filtro, setFiltro] = useState("");
  const t = useT();

  // Il filtro vale per la cartella che si sta guardando: entrando in un'altra riparte vuoto.
  const setPercorso = (nuovo: string) => {
    setPercorsoGrezzo(nuovo);
    setFiltro("");
  };
  const { data, error, loading, reload } = useQuery<ElencoCartelle>(
    `/impostazioni/cartelle?percorso=${encodeURIComponent(percorso)}`,
  );

  const creazione = useAction(async () => {
    await api.post("/impostazioni/cartelle", { percorso: percorso ? `${percorso}/${nome}` : nome });
    setNome("");
    reload();
  });

  const livelli = percorso ? percorso.split("/") : [];
  const cercato = filtro.trim().toLowerCase();
  const visibili = (data?.cartelle ?? []).filter((c) => c.nome.toLowerCase().includes(cercato));

  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-medium">{t.t("cartelle.titolo")}</h2>
        <p className="text-sm tenue">{t.t("cartelle.intro")}</p>
      </div>

      <Apribile titolo={t.t("impostazioni.comeFunziona")}>
        <p className="text-sm">{t.t("cartelle.comeFunziona")}</p>
      </Apribile>

      <ErrorNotice error={error} />
      {loading && !data && <Spinner className="tenue" />}

      {data && data.radice === null && (
        <Card className="px-4 py-6 text-center text-sm tenue">{t.t("cartelle.nonAttiva")}</Card>
      )}

      {data && data.radice !== null && (
        <Card className="divide-y overflow-hidden">
          <nav className="flex flex-wrap items-center gap-1 px-4 py-3 font-mono text-[13px]">
            <button className="break-all text-left hover:underline" onClick={() => setPercorso("")}>
              {data.radice}
            </button>
            {livelli.map((livello, i) => (
              <span key={i} className="flex items-center gap-1">
                <span className="tenue">/</span>
                <button
                  className="hover:underline"
                  onClick={() => setPercorso(livelli.slice(0, i + 1).join("/"))}
                >
                  {livello}
                </button>
              </span>
            ))}
          </nav>

          {data.cartelle.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-2">
              <Input
                className="min-w-0 flex-1 py-1.5 text-[13px]"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                placeholder={t.t("cartelle.filtro")}
              />
              <span className="shrink-0 text-xs tenue">
                {cercato
                  ? t.t("cartelle.contoFiltrato", { n: visibili.length, totale: data.cartelle.length })
                  : t.t("cartelle.conto", { n: data.cartelle.length })}
              </span>
            </div>
          )}

          {data.cartelle.length === 0 && (
            <p className="px-4 py-4 text-sm tenue">{t.t("cartelle.nessuna")}</p>
          )}

          {data.cartelle.length > 0 && visibili.length === 0 && (
            <p className="px-4 py-4 text-sm tenue">{t.t("cartelle.nessunaFiltro")}</p>
          )}

          {/* L'elenco scorre per conto suo: con centinaia di clienti il modulo
              di creazione qui sotto resta sempre a portata di mano. */}
          {visibili.length > 0 && (
            <div className="grid max-h-[28rem] content-start overflow-y-auto sm:grid-cols-2 xl:grid-cols-3">
              {visibili.map((cartella) => (
                <RigaCartella
                  key={cartella.percorso}
                  cartella={cartella}
                  onApri={() => setPercorso(cartella.percorso)}
                  onCambiato={reload}
                />
              ))}
            </div>
          )}

          <form
            className="flex flex-wrap items-center gap-3 px-4 py-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (nome.trim()) void creazione.run();
            }}
          >
            <Input
              className="min-w-0 flex-1 font-mono text-[13px]"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder={t.t("cartelle.nuovaSegnaposto")}
            />
            <Button variant="primary" type="submit" loading={creazione.running} disabled={!nome.trim()}>
              {t.t("cartelle.crea")}
            </Button>
          </form>

          {creazione.error ? (
            <div className="px-4 py-3">
              <ErrorNotice error={creazione.error} />
            </div>
          ) : null}
        </Card>
      )}
    </section>
  );
}

function RigaCartella({
  cartella,
  onApri,
  onCambiato,
}: {
  cartella: Cartella;
  onApri: () => void;
  onCambiato: () => void;
}) {
  const eliminazione = useAction(async () => {
    await api.delete(`/impostazioni/cartelle?percorso=${encodeURIComponent(cartella.percorso)}`);
    onCambiato();
  });
  const t = useT();

  return (
    <div className="min-w-0 space-y-2 border-b px-4 py-1.5 hover:bg-[var(--superficie-tenue)]">
      <div className="flex items-center gap-3 text-sm">
        <button
          className="min-w-0 truncate text-left font-mono text-[13px] hover:underline"
          title={cartella.nome}
          onClick={onApri}
        >
          {cartella.nome}/
        </button>
        <Badge>
          {cartella.uid}:{cartella.gid} · {cartella.permessi}
        </Badge>

        {/* Un pulsante pieno raddoppierebbe l'altezza della riga: qui ne servono tante a vista. */}
        <button
          className="ml-auto shrink-0 text-xs text-red-600 hover:underline disabled:opacity-50"
          disabled={eliminazione.running}
          onClick={() => {
            if (window.confirm(t.t("cartelle.eliminaConferma", { nome: cartella.nome }))) {
              void eliminazione.run();
            }
          }}
        >
          {t.t(eliminazione.running ? "cartelle.elimino" : "comune.elimina")}
        </button>
      </div>

      <ErrorNotice error={eliminazione.error} />
    </div>
  );
}

/* --- Database -------------------------------------------------------------- */

/** Esito di un ripristino; il messaggio da mostrare lo sceglie l'interfaccia, nella sua lingua. */
interface EsitoRipristino {
  readonly salvataggio: string;
}

function SezioneDatabase() {
  const scelta = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [esito, setEsito] = useState<EsitoRipristino | null>(null);
  const t = useT();

  const ripristino = useAction(async (daCaricare: File) => {
    // Il corpo è il file grezzo: `api` parla JSON, qui serve altro.
    const risposta = await fetch("/api/impostazioni/ripristino", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/vnd.sqlite3" },
      body: daCaricare,
    });

    if (!risposta.ok) throw await toApiError(risposta);

    setEsito((await risposta.json()) as EsitoRipristino);
    setFile(null);
  });

  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-medium">{t.t("database.titolo")}</h2>
        <p className="text-sm tenue">{t.t("database.intro")}</p>
      </div>

      <Apribile titolo={t.t("impostazioni.comeFunziona")}>
        <p className="text-sm">{t.t("database.comeFunziona")}</p>
      </Apribile>

      <Card className="space-y-5 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{t.t("database.scarica")}</p>
            <p className="text-xs tenue">{t.t("database.scaricaAiuto")}</p>
          </div>

          {/* Un collegamento vero: il cookie di sessione basta ad autorizzarlo,
              e il browser sa scaricare meglio di quanto sappiamo farlo noi. */}
          <a
            href="/api/impostazioni/backup"
            download
            className="inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors superficie hover:bg-[var(--superficie-tenue)]"
          >
            {t.t("database.scaricaBackup")}
          </a>
        </div>

        <div className="space-y-3 border-t pt-5">
          <div>
            <p className="text-sm font-medium">{t.t("database.ripristina")}</p>
            <p className="text-xs tenue">{t.t("database.ripristinaAiuto")}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={scelta}
              type="file"
              accept=".db,application/vnd.sqlite3,application/octet-stream"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setEsito(null);
              }}
              // Un campo file ha una larghezza propria, che su schermi molto
              // stretti esce dalla pagina: qui non supera mai la sua colonna.
              className="max-w-full text-sm file:mr-3 file:rounded-lg file:border file:bg-[var(--superficie)] file:px-3 file:py-1.5 file:text-sm"
            />

            <Button
              variant="danger"
              className="ml-auto"
              disabled={file === null}
              loading={ripristino.running}
              onClick={() => {
                if (!file) return;

                const conferma = window.confirm(t.t("database.ripristinaConferma", { file: file.name }));

                if (conferma) void ripristino.run(file);
              }}
            >
              {t.t("storico.ripristina")}
            </Button>
          </div>

          <ErrorNotice error={ripristino.error} />

          {esito && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/8 px-3.5 py-2.5 text-sm text-emerald-700 dark:text-emerald-400">
              {t.t("server.ripristinato")}
            </div>
          )}
        </div>
      </Card>
    </section>
  );
}
